/**
 * Safe Zustand store factory for React 19 / Next.js 15.
 *
 * Problem: Zustand v5's default `create` wraps the selector in
 * `useCallback([api, selector])`. Since selectors are usually inline
 * arrow functions, `useCallback` returns a NEW getSnapshot function
 * every render. Combined with React 19's concurrent rendering, this
 * causes Error #185 (infinite update loop during commit phase).
 *
 * Solution: Use `createWithEqualityFn` from `zustand/traditional`
 * which relies on `useSyncExternalStoreWithSelector` — a battle-tested
 * shim that:
 *  1. Passes stable `api.getState` as the snapshot function
 *  2. Handles selector memoization internally (no useCallback)
 *  3. Supports custom equality functions
 *
 * Usage: Replace `import { create } from "zustand"` with
 *        `import { create } from "@/lib/create-safe-store"`
 *
 * All component code stays the same:
 *   const value = useMyStore((s) => s.value)  // works safely
 *   useMyStore.getState().doSomething()        // works as before
 */

import { createWithEqualityFn } from "zustand/traditional";
import type { StateCreator } from "zustand";

export function create<T>(initializer: StateCreator<T, [], []>) {
  return createWithEqualityFn<T>(initializer, Object.is);
}
